/// <reference path="guid.ts" />
/// <reference path="../../app.settings.ts" />

module Meevo.Components.BaseClasses {

    /**
     * Concrete implementation of IDataService.
     * Provides core functionality of maintenance forms.
     */
    export abstract class DataServiceBase implements IDataService {

        get Promise(): angular.IQService {
            return this.$q;
        }

        constructor(protected appSettings: Meevo.Components.Common.IAppSettings,
            protected $http: angular.IHttpService,
            protected $q: angular.IQService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected $cookies: angular.cookies.ICookiesService)
        {

        }

        /**
        * Returns whether or not the request meets validation requirements.
        * @param {Model.ClientValidate} request
        * @return {IPromise<boolean>}
        */
        validate(baseEndpoint: string, request: any) {
            let uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/Validation/`;
            return this.$http.post<boolean>(uri, request)
                .then((response) => response.data);
        }

        /**
        * Retrieves item data by id
        * @param {string} baseEndpoint - base end point
        * @param {Guid} [id] - item id
        * @param {string} [sectionEndpoint] - section end point
        * @return {IPromise<T>}
        */
        getItem<T, TSearch>(baseEndpoint: string, id?: Guid, sectionEndpoint?: string): angular.IPromise<T> {
            let uri = '';

            if (id && sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}`;
            } else if (id) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}`;
            }

            return this.$http.get<T>(uri)
                .then((response) => response.data);
        }

        /**
        * Retrieves search results for a maintenance form item based on the supplied request.
        * @param {Model.SearchPage<T, TSearch>} request
        * @return {IPromise<T>}
        */
        getListPaged<T, TSearch extends Model.SearchCriteria>(baseEndpoint: string, request: Model.SearchPage<T, TSearch>): angular.IPromise<T[]>;
        getListPaged<T, TSearch>(baseEndpoint: string, request: Model.SearchPage<T, TSearch>, id?: Guid, sectionEndpoint?: string, requestConfig?: Object): angular.IPromise<T[]> {
            let uri = "";
            if (id && sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}/List/`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/List/`;
            }

            if (!requestConfig) {
                requestConfig = {};
            }

            return this.$http.post<T[]>(uri, request, requestConfig)
                .then((response) => response.data);
        }

        getCount<TSearch extends Model.SearchCriteria>(baseEndpoint: string, request: Model.SearchCount<TSearch>): angular.IPromise<number>;
        getCount<TSearch>(baseEndpoint: string, request: Model.SearchCount<TSearch>, id?: Guid, sectionEndpoint?: string): angular.IPromise<number> {

            let uri = "";
            if (id && sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}/Count/`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/Count/`;
            }

            return this.$http.post<number>(uri, request)
                .then((response) => response.data);
        }

        /**
        * Retrieves a maintenance form section for an item matching the supplied id at the specified endpoint.
        * @param {Guid} id
        * @param {string} endpoint
        * @return {IPromise<any>}
        */
        getSectionById(baseEndpoint: string, id: Guid, sectionEndpoint: string): angular.IPromise<any>;
        getSectionById(baseEndpoint: string, id: Guid, sectionEndpoint: string, asLocationId?: number): angular.IPromise<any> {

            var localParams: any = {};
            if (asLocationId !== null)
                localParams.asLocationId = asLocationId;

            return this.$http.get(`/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}`, { params: localParams })
                .then((response) => response.data);
        }

        /**
       * Deletes the item corresponding to the supplied id.
       * @param {Guid} id
       * @return {IPromise<any>}
       */

        delete(baseEndpoint: string, id: Guid, entityVersion: number, asLocationId?: number): angular.IPromise<any>;
        delete(baseEndpoint: string, id: Guid, entityVersion: number, asLocationId?: number, sectionEndpoint?: string, sectionItemId?: Guid, params = {}): angular.IPromise<any> {
            let uri = "";
            if (sectionEndpoint && sectionItemId) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}/${sectionItemId}`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}`;
            }

            var localParams: any = { entityVersion: entityVersion };
            if (asLocationId !== null)
                localParams.asLocationId = asLocationId;

            angular.extend(localParams, params);

            return this.$http.delete(uri, {
                params: localParams
            })
                .then((response) => response.data);
        }

        deleteWithJsonBody(baseEndpoint: string, data: any): angular.IPromise<any> {
            let uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/`;
            return this.customMethodData(uri, 'DELETE', data)
                .then((res) => {
                    if (res.status == 409) {
                        var data: any = res.data;
                        data.isWarning = true;
                    }
                    return res.data;
                });
        }

        deleteFile(baseEndpoint: string, data: any): angular.IPromise<any>;
        deleteFile(baseEndpoint: string, data: any, sectionEndpoint?: string, sectionItemId?: Guid): angular.IPromise<any> {
            let uri = "";

            console.log("identity: ", this.identityService.current());
            let identity = this.identityService.current();
            document.cookie = "ssid=" + identity.id;
            if (sectionEndpoint && sectionItemId) {
                uri = `${baseEndpoint}${sectionEndpoint}/${sectionItemId}`;
            } else {
                uri = `${baseEndpoint}`;
            }
            let jwtToken = sessionStorage.getItem('mil:meevo:token');

            return this.$http.delete(
                uri,
                {
                    data: data,
                     withCredentials: true,
                     headers: {
                    "Content-Type": "application/json;charset=utf-8",
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${jwtToken}`,
                }
            }).then((response) => response.data);
        }

        /**
* Allows the user to customize an API call his their specific needs.
* params: uri, method & data
* @returns {}
*/
        customMethodData(uri: string, method: string, data: any): angular.IPromise<any>;
        customMethodData(uri: string, method: string, data: any, params?: any): angular.IPromise<any>;
        customMethodData(uri: string, method: string, data: any, params?: any): angular.IPromise<any> {
            if (params) {
                return this.$http({
                    url: uri,
                    method: method,
                    params: params,
                    data: data,
                    headers: { "Content-Type": "application/json;charset=utf-8" }
                })
                    .then((res) => {
                        return res;
                    });
            }
            else {
                return this.$http({
                    url: uri,
                    method: method,
                    data: data,
                    headers: { "Content-Type": "application/json;charset=utf-8"}
                })
                    .then((res) => {
                        return res;
                    });
            }

        }

        getTotalCount(baseEndpoint: string): angular.IPromise<any> {
            return this.$http.get(`/${this.appSettings.API_ROOT}/${baseEndpoint}/totalCount`)
                .then((response) => response.data);
        }

        /**
        * Undeletes the item corresponding to the supplied id.
        * @param {Guid} id
        * @return {IPromise<any>}
        */
        undelete(baseEndpoint: string, id: Guid, entityVersion: number, asLocationId?: number): angular.IPromise<any>;
        undelete(baseEndpoint: string, id: Guid, entityVersion: number, asLocationId?: number, sectionEndpoint?: string, sectionItemId?: Guid): angular.IPromise<any> {
            let uri = "";
            if (sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}/${sectionItemId}/undelete`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/undelete/`;
            }

            var localParams: any = { entityVersion: entityVersion };
            if (asLocationId !== null)
                localParams.asLocationId = asLocationId;

            return this.$http.delete(uri, {
                params: localParams
            })
                .then((response) => response.data);
        }

        /**
        * Persists an add for the supplied entity.
        * @param {any} entity
        * @return {IPromise<any>}
        */
        add(baseEndpoint: string, entity: any): angular.IPromise<any>;
        add(baseEndpoint: string, entity: any, id?: Guid, sectionEndpoint?: string): angular.IPromise<any> {
            let uri = "";

            if (sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}`;
            }
            return this.$http.post(uri, entity)
                .then((response) => {
                    if (response.status == 409) {
                        var data: any = response.data;
                        data.isWarning = true;
                    }
                    return response.data;
                });
        }

        addFile(baseEndpoint: string, entity: any): angular.IPromise<any>;
        addFile(baseEndpoint: string, entity: any, id?: Guid, sectionEndpoint?: string): angular.IPromise<any> {
            let uri = "";

            console.log("identity: ", this.identityService.current());
            let identity = this.identityService.current();
            //let cookie = this.$cookies.put("ssid", identity.id);

            document.cookie = "ssid=" + identity.id;
            console.log("COOKIES!: ",this.$cookies.getAll());
            if (sectionEndpoint) {
                uri = `${baseEndpoint}${id}/${sectionEndpoint}`;
            } else {
                uri = `${baseEndpoint}`;
            }
            //withCredentials: true,
            let jwtToken = sessionStorage.getItem('mil:meevo:token');

            return this.$http.post(`${uri}`, entity, {
                withCredentials: true, headers: {
                    "Content-Type": "application/json",
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${jwtToken}`,
                }
            })
                .then((response) => {
                    if (response.status == 409) {
                        var data: any = response.data;
                        data.isWarning = true;
                    }
                    return response.data;
                });
        }
        /**
        * Persists an update for the supplied entity.
        * @param {T} entity
        * @param {Guid} id
        * @param {string} sectionEndpoint
        * @return {IPromise<T>}
        */
        update(baseEndpoint: string, entity: any): angular.IPromise<any>;
        update(baseEndpoint: string, entity: any, id?: Guid, sectionEndpoint?: string, asLocationId?: number): angular.IPromise<any> {
            let uri = "";
            if (sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/`;
            }

            var localParams: any = {};
            if (asLocationId !== undefined && asLocationId !== null)
                localParams.asLocationId = asLocationId;

            return this.$http.put(uri, entity, {
                params: localParams
            })
                .then((response) => {
                    if (response.status == 409) {
                        var data: any = response.data;
                        data.isWarning = true;
                    }
                    if (response.status == 422) {
                        var data: any = response.data;
                        data.isValidationException = true;
                    }
                    return response.data;

                });
        }

        /**
         * Patches the object state member for the item corresponding to the supplied id
         * @param baseEndpoint
         * @param id
         * @param entityVersion
         * @param objectState
         * @param asLocationId
         */
        objectStatePatch(baseEndpoint: string, id: Guid, entityVersion: number, objectState: number, asLocationId?: number): angular.IPromise<any>;
        objectStatePatch(baseEndpoint: string, id: Guid, entityVersion: number, objectState: number, asLocationId?: number, sectionEndpoint?: string, suppressWarnings?: boolean): angular.IPromise<any> {
          let uri = "";
          if (sectionEndpoint) {
            uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}`;
          } else {
            uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}`;
          }

          var localParams: any = { entityVersion: entityVersion, objectState: objectState };
          if (asLocationId !== null)
            localParams.asLocationId = asLocationId;

          if (suppressWarnings !== null)
            localParams.suppressWarnings = suppressWarnings;

          return this.customMethodData(uri, 'PATCH', localParams)
            .then((res) => {
              if (res.status == 409) {
                var data: any = res.data;
                data.isWarning = true;
              }
              return res.data;
            });
        }

        /**
        * Persists a patch for the supplied entity.
        * @param {T} entity
        * @param {Guid} id
        * @param {string} sectionEndpoint
        * @return {IPromise<T>}
        */
        patchArray(baseEndpoint: string, entity: any): angular.IPromise<any>;
        patchArray(baseEndpoint: string, entity: any, id?: Guid, sectionEndpoint?: string): angular.IPromise<any> {
            let uri = "";
            if (sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/`;
            }

            return this.$http({
                method: 'PATCH', url: uri, data: entity, transformRequest: function (data, headerGetter) {
                    var output = [];
                    for (var i = 0; i < _.keys(data).length; i++) {
                        output.push(data[i]);
                    }
                    return angular.toJson(output);
                }
            }).then((response) => response.data);
        }

        patch(baseEndpoint: string, entity: any): angular.IPromise<any>;
        patch(baseEndpoint: string, entity: any, id?: Guid): angular.IPromise<any>;
        patch(baseEndpoint: string, entity: any, id?: Guid, sectionEndpoint?: string): angular.IPromise<any>;
        patch(baseEndpoint: string, entity: any, id?: Guid, sectionEndpoint?: string): angular.IPromise<any> {
            let uri = "";
            if (sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}`;
            } else if (id) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/`;
            }
            return this.$http.patch(uri, entity)
                .then((res) => {
                    if (res.status == 409) {
                        var data: any = res.data;
                        data.isWarning = true;
                    }
                    return res.data;
                });
        }

        /**
         * Cache used to store enum values.
         */
        private static enumCache: Meevo.Components.BaseClasses.ICache<Array<Model.EnumDto>> = {};

        /**
        * Retrieves a list of enumerations belonging to the supplied enumType
        * This will cache the values for future reference.
        * @param {Model.EnumTypeEnum} - enumType
        * return {Model.EnumDto[]>}
        */
        getEnumList(...enumTypes: Model.EnumTypeEnum[]): angular.IPromise<Model.EnumDto[]> {
            let deferred = this.$q.defer<Model.EnumDto[]>();
            let listOfEnums: Model.EnumDto[] = [];

            let uncachedEnumTypes: Model.EnumTypeEnum[] = [];
            enumTypes.forEach((enumType, ndx) => {
                let cachedEnum = DataServiceBase.enumCache[enumType.toString()];
                if (_.isUndefined(cachedEnum)) {
                    uncachedEnumTypes.push(enumType);
                }
                else {
                    listOfEnums.push(...cachedEnum);
                }
            });

            if (uncachedEnumTypes.length > 0) {

                /*  Use this.appSettings.APP_LANGUAGEID When DB Is Ready    */
                let languageId = this.appSettings.APP_LANGUAGEID;

                this.$http.get(`/${this.appSettings.API_ROOT}/System/Enums/?enumTypes=${uncachedEnumTypes.join(",")}&languageId=${languageId}`)
                    .then((response) => {
                        let enums = _.chain(response.data).each((item: Model.EnumDto) => { item.labelId = "ENUM_LABEL_" + item.id; }).groupBy((item: Model.EnumDto) => { return item.enumTypeId; }).value();
                        for (var key in enums) {
                            DataServiceBase.enumCache[key.toString()] = <Array<Model.EnumDto>>enums[key];
                            listOfEnums.push(...<Array<Model.EnumDto>>enums[key]);
                        }
                        deferred.resolve(listOfEnums);
                    });
            }
            else {
                deferred.resolve(listOfEnums);
            }

            return deferred.promise;
        }

        /**
        * Retrieves a list of enumerations belonging to the supplied enumType
        * This will cache the values for future reference.
        * @param {Model.EnumTypeEnum} - enumType
        * return {Model.EnumDto[]>}
        */
        getSettings<T>(settingType: Model.BusinessSettingCategoryEnum, location:any): angular.IPromise<T> {
            let deferred = this.$q.defer<T>();
            var settingUrl = `/${this.appSettings.API_ROOT}/settings/${Model.BusinessSettingCategoryEnum[settingType]}`;
            if (location) {
                settingUrl = settingUrl + '?asLocationId=' + location.id;
            }
            this.$http.get(settingUrl)
                .then((response) => {
                    deferred.resolve(<T>response.data);
                });

            return deferred.promise;
        }

        /**
         * Gets a template represenation of the object at the specified endpoint.
         * @param baseEndpoint
         * @param {Guid} id
         * @param {string} sectionEndpoint
         * @return {IPromise<T>}
        */
        getTemplate(baseEndpoint: string): angular.IPromise<any>;
        getTemplate(baseEndpoint: string, id?: Guid, sectionEndpoint?: string): angular.IPromise<any>;
        getTemplate(baseEndpoint: string, id?: Guid, sectionEndpoint?: string, localParams?: any): angular.IPromise<any> {
            let uri = "";

            if (sectionEndpoint) {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/${id}/${sectionEndpoint}/template`;
            } else {
                uri = `/${this.appSettings.API_ROOT}/${baseEndpoint}/template`;
            }

            return this.$http.get(uri, { params: localParams })
                .then((response) => response.data);
        }

        /**
         * Creates a usage tracking entry.
         * @param noun
         * @param verb
         * @param details
         */
        trackUsage(noun: Meevo.Model.NounEnum, verb: Meevo.Model.VerbEnum, details: any): angular.IPromise<any>;
        trackUsage(noun: Meevo.Model.NounEnum, verb: Meevo.Model.VerbEnum, details: any, mainEntityId?: Guid | string): angular.IPromise<any>;
        trackUsage(noun: Meevo.Model.NounEnum, verb: Meevo.Model.VerbEnum, details: any, mainEntityId?: Guid | string, shortDescription?: string): angular.IPromise<any> {

            let request: any = {
                noun: noun,
                verb: verb,
                mainEntityId: mainEntityId,
                details: details,
                shortDescription: shortDescription
            };

            return this.$http.post(`/${this.appSettings.API_ROOT}/UsageTracking`, request)
                .then((response) => response.data);
        }
    }
}